import { useState } from "react";
import { X, Send, CheckCircle2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { useCourseOptions } from "@/lib/useCourseOptions";

export default function EnquiryModal({ open, onClose }) {
  const options = useCourseOptions();
  const [form, setForm] = useState({ name: "", mobile: "", course: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, course: form.course || options[0], source: "modal" });
      setDone(true);
      toast.success("Enquiry submitted! We'll get in touch shortly.");
      setForm({ name: "", mobile: "", course: "", message: "" });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4" data-testid="enquiry-modal">
      <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden fade-up">
        <div className="bg-[#1E3A8A] text-white px-7 py-6 relative">
          <button
            onClick={close}
            data-testid="enquiry-close-btn"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="text-xs tracking-[0.18em] uppercase text-teal-300 font-semibold">Enquire Now</div>
          <h3 className="mt-2 text-2xl font-bold leading-snug">Start your Pharma career today</h3>
          <p className="mt-1 text-sm text-slate-200/80">Fill the form, our team will reach out shortly.</p>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto text-teal-700">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h4 className="mt-4 font-bold text-lg text-[#0F172A]">Thank you!</h4>
            <p className="text-sm text-slate-600 mt-1">Your enquiry has been received. We'll call you soon.</p>
            <button onClick={close} className="btn-primary mt-6" data-testid="enquiry-success-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 space-y-4" data-testid="enquiry-form">
            <Field
              label="Full Name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Your full name"
              testid="enquiry-name-input"
              required
            />
            <Field
              label="Mobile Number"
              type="tel"
              value={form.mobile}
              onChange={(v) => setForm({ ...form, mobile: v.replace(/[^\d+ -]/g, "") })}
              placeholder="+91 9XXXXXXXXX"
              testid="enquiry-mobile-input"
              required
            />
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Interested Course</label>
              <select
                value={form.course}
                onChange={(e) => setForm({ ...form, course: e.target.value })}
                data-testid="enquiry-course-select"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
              >
                <option value="">Select a course...</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your Message / Query</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what you'd like to know..."
                rows={3}
                data-testid="enquiry-message-input"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="enquiry-submit-btn"
              className="btn-accent w-full disabled:opacity-60"
            >
              {submitting ? "Submitting..." : <>Send Enquiry <Send className="w-4 h-4" /></>}
            </button>
            <p className="text-[11px] text-slate-400 text-center">By submitting, you agree to be contacted by Sunbright Pharma Academy.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
      />
    </div>
  );
}
